import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useApiClient } from '../api/client';
import { Button, Card, Alert, PageHeader, Badge, PromptTextarea } from '../components/ui';
import { Header } from '../components/Header';

function ColorizeText(text: string): React.ReactNode {
  if (!text) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /"([^"]*)"/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    parts.push(
      <span key={`dialogue-${match.index}`} style={{ color: 'var(--color-dialogue)' }}>
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function Prose({ text }: { text: string }) {
  return (
    <div style={{ lineHeight: 1.5 }}>
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p style={{ margin: '0 0 8px 0' }}>
              {React.Children.map(children, child =>
                typeof child === 'string' ? ColorizeText(child) : child
              )}
            </p>
          ),
          ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

interface Turn {
  id: string;
  user_input: string;
  dm_output: string;
  created_at: string;
}

interface Campaign {
  id: string;
  title: string;
  category: string;
  status: string;
  summary: string;
}

interface CampaignDetail {
  campaign: Campaign;
  recent_turns: Turn[];
  current_location: { id: string; name: string } | null;
  turn_count: number;
}

export function CampaignPlay() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const api = useApiClient();

  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInput, setUserInput] = useState('');
  const [playing, setPlaying] = useState(false);
  const [maxTurns, setMaxTurns] = useState<number | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/sign-in');
      return;
    }
    if (isLoaded && isSignedIn && campaignId) {
      loadCampaign();
      loadConfig();
    }
  }, [isLoaded, isSignedIn, campaignId, navigate]);

  async function loadConfig() {
    try {
      const data = (await api.get('/config')) as { max_turns_per_campaign: number };
      setMaxTurns(data.max_turns_per_campaign);
    } catch (err) {
      // Silent fallback: no limit applied if config fetch fails
    }
  }

  async function loadCampaign() {
    try {
      const data = (await api.get(`/campaigns/${campaignId}`)) as CampaignDetail;
      setDetail(data);
      setError('');
    } catch (err) {
      setError(`Failed to load campaign: ${err}`);
    } finally {
      setLoading(false);
    }
  }

  async function handlePlayTurn() {
    if (!userInput.trim()) {
      setError('Please enter an action');
      return;
    }
    setPlaying(true);
    try {
      const result = (await api.post(`/campaigns/${campaignId}/turns`, {
        user_input: userInput,
      })) as Turn;

      setUserInput('');
      await loadCampaign();
      setError('');
    } catch (err) {
      setError(`Failed to play turn: ${err}`);
    } finally {
      setPlaying(false);
    }
  }

  if (!isLoaded || !isSignedIn) {
    return <div style={{ padding: 'var(--space-4)' }}>Loading...</div>;
  }

  if (loading) {
    return <div style={{ padding: 'var(--space-4)' }}>Loading campaign...</div>;
  }

  if (!detail) {
    return (
      <>
        <Header />
        <div style={{ padding: 'var(--space-4)' }}>Campaign not found</div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ padding: 'var(--space-4)', maxWidth: '1000px', margin: '0 auto' }}>
      <PageHeader
        title={detail.campaign.title}
        backButton={{ label: 'Campaigns', onClick: () => navigate('/campaigns') }}
      />

      <Card style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <strong>Category:</strong>{' '}
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {detail.campaign.category}
          </span>
        </div>
        {detail.campaign.summary && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <strong>Summary:</strong>
            <Prose text={detail.campaign.summary} />
          </div>
        )}
        {detail.current_location && (
          <div>
            <strong>Location:</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {detail.current_location.name}
            </span>
          </div>
        )}
      </Card>

      {error && <Alert variant="error">{error}</Alert>}

      {maxTurns !== null && detail && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Badge
            variant={
              detail.turn_count >= maxTurns
                ? 'danger'
                : detail.turn_count / maxTurns > 0.9
                  ? 'warning'
                  : 'default'
            }
          >
            {detail.turn_count} / {maxTurns} turns
          </Badge>
        </div>
      )}

      <Card style={{ marginBottom: 'var(--space-6)', minHeight: '200px' }}>
        <h3 style={{ marginTop: 0, marginBottom: 'var(--space-4)' }}>Story Log</h3>
        {detail.recent_turns.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>No turns yet. Take your first action!</p>
        ) : (
          detail.recent_turns.map(turn => (
            <div
              key={turn.id}
              style={{
                marginBottom: 'var(--space-4)',
                paddingBottom: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <p className="turn-user-input" style={{ margin: 0, marginBottom: 'var(--space-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-cyan)' }}>
                <strong>You:</strong> {turn.user_input}
              </p>
              <div style={{ fontFamily: 'var(--font-sans)' }}>
                <Prose text={turn.dm_output} />
              </div>
            </div>
          ))
        )}
      </Card>

      <div>
        {detail && maxTurns !== null && detail.turn_count >= maxTurns ? (
          <Alert variant="warning">
            Turn limit reached for this campaign. Start a new campaign to continue playing.
          </Alert>
        ) : (
          <div>
            <div
              style={{
                backgroundColor: 'rgba(45, 226, 230, 0.05)',
                color: 'var(--color-text-secondary)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--space-3)',
                fontSize: 'var(--text-xs)',
                lineHeight: 1.5,
                opacity: userInput.trim() ? 0.5 : 0.8,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              Your conversations are saved to let you resume your campaigns. Please avoid sharing sensitive personal information.{' '}
              <a
                href="/privacy"
                style={{
                  color: 'var(--color-info)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                See our Privacy Policy for details.
              </a>
            </div>
            <div className="turn-input-row" style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
              <PromptTextarea
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                placeholder="What do you do?"
                style={{ flex: 1 }}
                disabled={playing}
              />
              <Button
                variant="primary"
                onClick={handlePlayTurn}
                disabled={playing}
                loading={playing}
                style={{ alignSelf: 'flex-start' }}
              >
                {playing ? 'Playing...' : 'Take Action'}
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
}
